import React, { useState } from "react";
import { Check } from "@opendate-app/setlist";

import { useLandingPage } from "../LandingPageBuilderContainer";
import { TEMPLATES, TEMPLATE_CATEGORIES, applyTemplate } from "../landingPageTemplates";
import { BlueprintCard, BlueprintGallery } from "../../shared/BlueprintGallery";

const styles = {
  surface: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    paddingTop: 18,
    boxSizing: "border-box",
  },
  mock: { width: "74%", background: "var(--sl-bg-primary)", borderRadius: "6px 6px 0 0", overflow: "hidden", display: "flex", flexDirection: "column" },
  hero: (accent) => ({ padding: "14px 12px", display: "flex", flexDirection: "column", gap: 6, alignItems: "center", textAlign: "center", background: accent }),
  logo: { padding: "3px 6px", borderRadius: 3, background: "var(--sl-bg-primary)", fontSize: 7, lineHeight: "9px", fontWeight: 800, letterSpacing: "0.04em", color: "var(--sl-text-primary)" },
  heroHeadline: { fontSize: 12, lineHeight: "15px", fontWeight: 700, color: "var(--sl-text-white)" },
  heroBar: { width: "80%", height: 12, borderRadius: 3, background: "var(--sl-bg-primary)" },
  heroBarFaded: { width: "50%", height: 12, borderRadius: 3, background: "var(--sl-bg-primary)", opacity: 0.6 },
  body: { padding: "10px 12px", display: "flex", flexDirection: "column", gap: 5 },
  block: { height: 44, borderRadius: 4, background: "var(--sl-bg-tertiary)" },
  line: (width) => ({ height: 5, width, borderRadius: 3, background: "var(--sl-bg-tertiary)" }),
  popular: { position: "absolute", top: 10, left: 10, padding: "2px 8px", borderRadius: 999, background: "var(--sl-bg-brand-primary)", border: "1px solid var(--sl-border-brand)", color: "var(--sl-text-brand)", fontSize: 12, lineHeight: "18px", fontWeight: 600 },
  check: { position: "absolute", top: 10, right: 10, width: 24, height: 24, borderRadius: 999, background: "var(--sl-bg-brand-solid)", display: "flex", alignItems: "center", justifyContent: "center" },
};

const venueInitials = (name) => {
  const words = (name || "").replace(/^the\s+/i, "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const short = words[0].length <= 4 ? words[0] : words.map((word) => word[0]).join("");
  return short.toUpperCase();
};

const CATEGORY_TABS = TEMPLATE_CATEGORIES.map((item) => ({
  value: item.label,
  label: `${item.label} (${item.label === "All" ? TEMPLATES.length : TEMPLATES.filter((template) => template.category === item.label).length})`,
}));

const TemplateStep = ({ setStep }) => {
  const { page, props, venueData, savePage } = useLandingPage();
  const [category, setCategory] = useState("All");

  const venueName = props.venueOwnerships.find((venue) => venue.id === page.venue_ownership_id)?.name || page.venue_name;
  const eventTitle = venueData.events[0]?.title;
  const context = { venueName, eventTitle, slug: page.slug };
  const templates = category === "All" ? TEMPLATES : TEMPLATES.filter((template) => template.category === category);

  const choose = async (template) => {
    if (template.key === page.template_key) {
      setStep("content");
      return;
    }

    const changes = applyTemplate(template, context);
    const saved = await savePage({ ...changes, settings: { ...changes.settings, create_segment: true } });
    if (saved) setStep("content");
  };

  const logo = venueInitials(venueName);

  return (
    <BlueprintGallery
      caption="What's this page for?"
      ariaLabel="Blueprint categories"
      tabs={CATEGORY_TABS}
      value={category}
      onChange={setCategory}
    >
      {templates.map((template) => {
        const selected = template.key === page.template_key;
        const accent = props.accents[template.accent];
        const headline = applyTemplate(template, context).settings.headline;

        return (
          <BlueprintCard
            key={template.key}
            name={template.name}
            description={template.sub}
            selected={selected}
            aria-pressed={selected}
            aria-label={template.name}
            onSelect={() => choose(template)}
          >
            <div style={styles.surface}>
              <div style={styles.mock}>
                <div style={styles.hero(accent)}>
                  {logo && <div style={styles.logo}>{logo}</div>}
                  <div style={styles.heroHeadline}>{headline}</div>
                  <div style={styles.heroBar} />
                  <div style={styles.heroBarFaded} />
                </div>
                <div style={styles.body}>
                  <div style={styles.block} />
                  <div style={styles.line("100%")} />
                  <div style={styles.line("65%")} />
                </div>
              </div>
            </div>
            {template.popular && <span style={styles.popular}>Popular</span>}
            {selected && (
              <span style={styles.check}>
                <Check width={14} height={14} color="var(--sl-text-white)" aria-label="Selected" />
              </span>
            )}
          </BlueprintCard>
        );
      })}
    </BlueprintGallery>
  );
};

export default TemplateStep;
